/**
 * Inline employee picker for Internal compose mode.
 * Lists all print-house employees (CRM), not only FrontDesk/Chatwoot agents.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { ConversationMember } from "@/widgets/frontdesk/providers/internal/types/direct-messages.types";
import { colorTokens } from "@/widgets/frontdesk/design-system";
import { useGomakeAxios } from "@/hooks";
import { useUserProfile } from "@/hooks/use-user-profile";

interface InternalEmployeePickerProps {
  selected: ConversationMember[];
  onChange: (users: ConversationMember[]) => void;
  error?: string;
  /** Hide users already in the conversation */
  excludeUserIds?: string[];
  /** Hide outer section padding/border when used inside a dialog */
  embedded?: boolean;
  /**
   * Fill parent height; only the employee list scrolls.
   * Use inside flex layouts (e.g. floating chat compose).
   */
  fillHeight?: boolean;
}

function extractEmployeeRows(res: any): any[] {
  const candidates = [
    res?.data?.data?.data,
    res?.data?.data,
    res?.data,
    res,
  ];
  for (const c of candidates) {
    if (Array.isArray(c)) return c;
  }
  return [];
}

function memberLabel(user: ConversationMember): string {
  return (
    user.name ||
    `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
    user.email ||
    user.userId
  );
}

function memberInitials(user: ConversationMember): string {
  const label = memberLabel(user);
  return (
    `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}` ||
    label[0] ||
    "?"
  ).toUpperCase();
}

function mapEmployeeToMember(raw: any): ConversationMember | null {
  // CRM uses `userID` (capital ID). Never fall back to employee `id` —
  // DM membership / JWT auth are keyed by GoMake userId.
  const userId = String(
    raw?.userID ||
      raw?.userId ||
      raw?.UserID ||
      raw?.UserId ||
      "",
  ).trim();
  if (
    !userId ||
    userId === "00000000-0000-0000-0000-000000000000"
  ) {
    return null;
  }

  const firstName = String(
    raw?.firstname || raw?.firstName || raw?.FirstName || "",
  ).trim();
  const lastName = String(
    raw?.lastname || raw?.lastName || raw?.LastName || "",
  ).trim();
  const email = String(raw?.email || raw?.Email || "").trim();
  const name =
    `${firstName} ${lastName}`.trim() ||
    String(raw?.name || raw?.Name || "").trim() ||
    email ||
    userId;
  const employeeId = String(
    raw?.employeeId ||
      raw?.EmployeeId ||
      raw?.id ||
      raw?.Id ||
      "",
  ).trim();

  return {
    userId,
    email,
    firstName,
    lastName,
    name,
    ...(employeeId ? { employeeId } : {}),
    isArchived: false,
    isFavorite: false,
    role: "member",
  };
}

function isActiveEmployee(raw: any): boolean {
  if (raw?.isPendingInvitation || raw?.IsPendingInvitation) return true;
  if (typeof raw?.isActive === "boolean") return raw.isActive;
  if (typeof raw?.IsActive === "boolean") return raw.IsActive;
  return true;
}

export const InternalEmployeePicker: React.FC<InternalEmployeePickerProps> = ({
  selected,
  onChange,
  error,
  excludeUserIds = [],
  embedded = false,
  fillHeight = false,
}) => {
  const { t } = useTranslation();
  const dir: "rtl" | "ltr" = t("direction") === "rtl" ? "rtl" : "ltr";
  const { callApi } = useGomakeAxios();
  const { profileState } = useUserProfile();

  const excludeKey = useMemo(
    () =>
      excludeUserIds
        .map((id) => String(id || "").trim().toLowerCase())
        .filter(Boolean)
        .sort()
        .join("|"),
    [excludeUserIds],
  );

  const excludedIds = useMemo(() => {
    const ids = [
      profileState?.id,
      profileState?.userId,
      profileState?.employeeId,
      ...excludeKey.split("|").filter(Boolean),
    ]
      .map((v) => String(v || "").trim().toLowerCase())
      .filter(Boolean);
    return new Set(ids);
  }, [
    profileState?.id,
    profileState?.userId,
    profileState?.employeeId,
    excludeKey,
  ]);

  const selfEmail = String(profileState?.email || "")
    .trim()
    .toLowerCase();

  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [allEmployees, setAllEmployees] = useState<ConversationMember[]>([]);

  const selectedIds = useMemo(
    () => new Set(selected.map((s) => s.userId)),
    [selected],
  );

  // Fetch once per mount / callApi — do not refetch when exclude list changes
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await callApi(
        "GET",
        "/v1/crm-service/employee/get-employees",
        {},
        false,
      );
      const rows = extractEmployeeRows(res);

      const mapped = rows
        .filter(isActiveEmployee)
        .map(mapEmployeeToMember)
        .filter((m): m is ConversationMember => Boolean(m))
        .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email));

      const byId = new Map<string, ConversationMember>();
      for (const m of mapped) {
        if (!byId.has(m.userId)) byId.set(m.userId, m);
      }
      setAllEmployees(Array.from(byId.values()));
    } catch {
      setAllEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [callApi]);

  useEffect(() => {
    void load();
  }, [load]);

  const users = useMemo(() => {
    const available = allEmployees.filter((user) => {
      if (excludedIds.has(String(user.userId).toLowerCase())) return false;
      if (selfEmail && String(user.email).toLowerCase() === selfEmail) {
        return false;
      }
      return true;
    });

    const q = query.trim().toLowerCase();
    if (!q) return available;
    return available.filter((user) => {
      const haystack = [
        user.name,
        user.firstName,
        user.lastName,
        user.email,
        `${user.firstName || ""} ${user.lastName || ""}`,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [allEmployees, excludedIds, query, selfEmail]);

  const allVisibleSelected =
    users.length > 0 && users.every((u) => selectedIds.has(u.userId));
  const someVisibleSelected =
    users.some((u) => selectedIds.has(u.userId)) && !allVisibleSelected;

  const toggle = (user: ConversationMember) => {
    if (selectedIds.has(user.userId)) {
      onChange(selected.filter((s) => s.userId !== user.userId));
    } else {
      onChange([...selected, user]);
    }
  };

  const removeSelected = (userId: string) => {
    onChange(selected.filter((s) => s.userId !== userId));
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      const visibleIds = new Set(users.map((u) => u.userId));
      onChange(selected.filter((s) => !visibleIds.has(s.userId)));
      return;
    }
    const byId = new Map(selected.map((s) => [s.userId, s]));
    for (const user of users) {
      byId.set(user.userId, user);
    }
    onChange(Array.from(byId.values()));
  };

  const clearAll = () => onChange([]);

  return (
    <Box
      sx={{
        px: embedded ? 0 : 4,
        py: embedded ? 0 : 3,
        borderBottom: embedded
          ? "none"
          : `1px solid ${colorTokens.neutral[200]}`,
        direction: dir,
        ...(fillHeight
          ? {
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
              height: "100%",
              overflow: "hidden",
            }
          : {}),
      }}
    >
      <Typography
        sx={{
          color: error ? "#DC2626" : colorTokens.neutral[700],
          fontSize: fillHeight ? "0.7rem" : "0.875rem",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          mb: fillHeight ? 0.75 : 1.5,
          textAlign: "start",
          flexShrink: 0,
        }}
      >
        {t("frontdesk.compose.employees", { defaultValue: "Employees" })}{" "}
        <Box component="span" sx={{ color: "#DC2626", fontWeight: 700 }}>
          *
        </Box>
      </Typography>

      {selected.length > 0 && (
        <Box
          sx={{
            mb: fillHeight ? 0.75 : 1.5,
            p: fillHeight ? 0.75 : 1.25,
            borderRadius: 1,
            border: `1px solid ${colorTokens.primary[200]}`,
            backgroundColor: colorTokens.primary[50],
            flexShrink: 0,
            maxHeight: fillHeight ? 52 : undefined,
            overflowY: fillHeight ? "auto" : undefined,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: fillHeight ? 0.5 : 1 }}
          >
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 600,
                color: colorTokens.primary[700],
              }}
            >
              {t("frontdesk.compose.selectedEmployees", {
                defaultValue: "{{count}} selected",
                count: selected.length,
              })}
            </Typography>
            <Button
              size="small"
              onClick={clearAll}
              sx={{
                minWidth: 0,
                px: 0.75,
                py: 0,
                minHeight: 22,
                fontSize: "0.7rem",
                textTransform: "none",
              }}
            >
              {t("frontdesk.compose.clearSelection", {
                defaultValue: "Clear",
              })}
            </Button>
          </Stack>
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5}>
            {selected.map((user) => (
              <Chip
                key={user.userId}
                size="small"
                avatar={
                  <Avatar
                    sx={{
                      width: fillHeight ? 18 : 24,
                      height: fillHeight ? 18 : 24,
                      fontSize: "0.6rem",
                    }}
                  >
                    {memberInitials(user)}
                  </Avatar>
                }
                label={memberLabel(user)}
                onDelete={() => removeSelected(user.userId)}
                sx={{
                  maxWidth: "100%",
                  height: fillHeight ? 22 : undefined,
                  backgroundColor: colorTokens.semantic.background.primary,
                  border: `1px solid ${colorTokens.primary[200]}`,
                  "& .MuiChip-label": {
                    px: fillHeight ? 0.5 : undefined,
                    fontSize: fillHeight ? "0.7rem" : undefined,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                  "& .MuiChip-deleteIcon": {
                    fontSize: fillHeight ? "0.85rem" : undefined,
                  },
                }}
              />
            ))}
          </Stack>
        </Box>
      )}

      <TextField
        fullWidth
        size="small"
        placeholder={t("frontdesk.compose.searchEmployees", {
          defaultValue: "Search employees...",
        })}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        error={Boolean(error)}
        helperText={error}
        FormHelperTextProps={{
          sx: { textAlign: "start", mx: 0 },
        }}
        inputProps={{ dir, style: { textAlign: "start" } }}
        sx={{
          direction: dir,
          flexShrink: 0,
          "& .MuiInputBase-root": {
            minHeight: fillHeight ? 32 : undefined,
          },
          "& .MuiInputBase-input": {
            py: fillHeight ? 0.6 : undefined,
            fontSize: fillHeight ? "0.8125rem" : undefined,
          },
        }}
      />

      <Box
        sx={{
          mt: fillHeight ? 0.75 : 1.5,
          ...(fillHeight
            ? {
                flex: 1,
                minHeight: 0,
                maxHeight: "none",
              }
            : {
                maxHeight: 280,
              }),
          overflowY: "auto",
          border: `1px solid ${
            error ? "#DC2626" : colorTokens.neutral[200]
          }`,
          borderRadius: 1,
          direction: dir,
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : users.length === 0 ? (
          <Typography
            sx={{
              p: 2,
              color: colorTokens.neutral[500],
              fontSize: "0.875rem",
              textAlign: "start",
            }}
          >
            {t("frontdesk.compose.noEmployeesFound", {
              defaultValue: "No employees found",
            })}
          </Typography>
        ) : (
          <>
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                px: fillHeight ? 1 : 1.5,
                py: fillHeight ? 0.15 : 0.5,
                borderBottom: `1px solid ${colorTokens.neutral[200]}`,
                backgroundColor: colorTokens.neutral[50],
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    sx={{ py: fillHeight ? 0.15 : undefined }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: fillHeight ? "0.75rem" : "0.8125rem",
                      fontWeight: 600,
                      textAlign: dir === "rtl" ? "right" : "left",
                    }}
                  >
                    {allVisibleSelected
                      ? t("frontdesk.compose.deselectAll", {
                          defaultValue: "Deselect all",
                        })
                      : t("frontdesk.compose.selectAll", {
                          defaultValue: "Select all",
                        })}
                    {` (${users.length})`}
                  </Typography>
                }
                labelPlacement="end"
                sx={{
                  m: 0,
                  width: "100%",
                  ml: 0,
                  mr: 0,
                  // Parent list box sets direction; do not also row-reverse
                  justifyContent: "flex-start",
                }}
              />
            </Box>
            <List dense disablePadding>
              {users.map((user) => {
                const checked = selectedIds.has(user.userId);
                const label = memberLabel(user);
                const textAlign = dir === "rtl" ? "right" : "left";
                return (
                  <ListItemButton
                    key={user.userId}
                    selected={checked}
                    onClick={() => toggle(user)}
                    sx={{
                      gap: fillHeight ? 0.75 : 1.25,
                      py: fillHeight ? 0.35 : 1,
                      px: fillHeight ? 1 : 1.5,
                      minHeight: fillHeight ? 38 : undefined,
                      alignItems: "center",
                      "&.Mui-selected": {
                        backgroundColor: colorTokens.primary[50],
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor: colorTokens.primary[100],
                      },
                    }}
                  >
                    <Checkbox
                      checked={checked}
                      tabIndex={-1}
                      disableRipple
                      size="small"
                      sx={{
                        p: fillHeight ? 0.25 : 0.5,
                        m: 0,
                        flexShrink: 0,
                      }}
                    />
                    <Avatar
                      sx={{
                        width: fillHeight ? 26 : 36,
                        height: fillHeight ? 26 : 36,
                        fontSize: fillHeight ? "0.65rem" : "0.75rem",
                        flexShrink: 0,
                        bgcolor: checked
                          ? colorTokens.primary[600]
                          : colorTokens.neutral[400],
                      }}
                    >
                      {memberInitials(user)}
                    </Avatar>
                    <ListItemText
                      primary={label}
                      secondary={user.email}
                      sx={{
                        flex: "1 1 auto",
                        minWidth: 0,
                        my: 0,
                        textAlign,
                      }}
                      primaryTypographyProps={{
                        fontSize: fillHeight ? "0.8125rem" : "0.875rem",
                        fontWeight: checked ? 600 : 400,
                        textAlign,
                        noWrap: true,
                        lineHeight: 1.2,
                      }}
                      secondaryTypographyProps={{
                        fontSize: fillHeight ? "0.68rem" : "0.75rem",
                        textAlign,
                        component: "span",
                        noWrap: true,
                        sx: {
                          display: "block",
                          width: "100%",
                          textAlign,
                          unicodeBidi: "plaintext",
                          lineHeight: 1.15,
                        },
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </>
        )}
      </Box>
    </Box>
  );
};
